import { evaluate } from "feelin";

export type InitialValues = {
  IV_joint: string;
  IV_ailment: string;
  IV_market: string;
};

/**
 * Parses a visibleWhen predicate and processes IV_ variables according to export rules:
 *
 * 1. If conditional only has IV_ variables (e.g., "IV_market = se"):
 *    - Evaluate the condition
 *    - If true: return { shouldInclude: true, newVisibility: "visible" }
 *    - If false: return { shouldInclude: false }
 *
 * 2. If conditional has both IV_ and other variables:
 *    - Evaluate only the IV_ part
 *    - If false: return { shouldInclude: false }
 *    - If true: return { shouldInclude: true, newVisibility: { visibleWhen: <predicate without IV_ parts> } }
 *
 * 3. If no IV_ variables: return { shouldInclude: true, newVisibility: <original> }
 */
export function processVisibleWhenForExport(
  visibility: "visible" | { visibleWhen: string },
  initialValues: InitialValues
): { shouldInclude: boolean; newVisibility?: "visible" | { visibleWhen: string } } {
  // If visibility is just "visible", no processing needed
  if (visibility === "visible") {
    return { shouldInclude: true, newVisibility: visibility };
  }

  const predicate = visibility.visibleWhen;

  // Check if predicate contains any IV_ variables
  const hasIVVariables = /\bIV_(joint|ailment|market)\b/.test(predicate);

  // If no IV_ variables, return as is
  if (!hasIVVariables) {
    return { shouldInclude: true, newVisibility: visibility };
  }

  // Split predicate by "and" to identify individual conditions
  // This is a simple parser - for production you might want something more robust
  const conditions = predicate.split(/\s+and\s+/);

  const ivConditions: string[] = [];
  const otherConditions: string[] = [];

  for (const condition of conditions) {
    if (/\bIV_(joint|ailment|market)\b/.test(condition)) {
      ivConditions.push(condition.trim());
    } else {
      otherConditions.push(condition.trim());
    }
  }

  // Evaluate IV_ conditions
  try {
    const context: Record<string, string> = {};
    Object.entries(initialValues).forEach(([key, value]) => {
      if (value) {
        context[key] = value;
      }
    });

    // Build a predicate from just the IV_ conditions
    const ivPredicate = ivConditions.join(" and ");
    const result = evaluate(ivPredicate, context);

    const ivConditionsPass = result && typeof result === "object" && "value" in result
      ? Boolean(result.value)
      : Boolean(result);

    // If IV_ conditions don't pass, exclude the question
    if (!ivConditionsPass) {
      return { shouldInclude: false };
    }

    // IV_ conditions pass
    // If there are only IV_ conditions, set visibility to "visible"
    if (otherConditions.length === 0) {
      return { shouldInclude: true, newVisibility: "visible" };
    }

    // If there are other conditions, return them
    const newPredicate = otherConditions.join(" and ");
    return { shouldInclude: true, newVisibility: { visibleWhen: newPredicate } };

  } catch (error) {
    console.error("Failed to evaluate IV_ conditions:", error);
    // On error, include the question with original visibility
    return { shouldInclude: true, newVisibility: visibility };
  }
}

/**
 * Similar processing for requirement predicates
 */
export function processRequirementForExport(
  requirement: "optional" | "required" | { requiredWhen: string },
  initialValues: InitialValues
): { shouldInclude: boolean; newRequirement?: "optional" | "required" | { requiredWhen: string } } {
  // If requirement is simple, no processing needed
  if (requirement === "optional" || requirement === "required") {
    return { shouldInclude: true, newRequirement: requirement };
  }

  const predicate = requirement.requiredWhen;

  // Check if predicate contains any IV_ variables
  const hasIVVariables = /\bIV_(joint|ailment|market)\b/.test(predicate);

  // If no IV_ variables, return as is
  if (!hasIVVariables) {
    return { shouldInclude: true, newRequirement: requirement };
  }

  // Split predicate by "and" to identify individual conditions
  const conditions = predicate.split(/\s+and\s+/);

  const ivConditions: string[] = [];
  const otherConditions: string[] = [];

  for (const condition of conditions) {
    if (/\bIV_(joint|ailment|market)\b/.test(condition)) {
      ivConditions.push(condition.trim());
    } else {
      otherConditions.push(condition.trim());
    }
  }

  // Evaluate IV_ conditions
  try {
    const context: Record<string, string> = {};
    Object.entries(initialValues).forEach(([key, value]) => {
      if (value) {
        context[key] = value;
      }
    });

    // Build a predicate from just the IV_ conditions
    const ivPredicate = ivConditions.join(" and ");
    const result = evaluate(ivPredicate, context);

    const ivConditionsPass = result && typeof result === "object" && "value" in result
      ? Boolean(result.value)
      : Boolean(result);

    // If IV_ conditions don't pass, this shouldn't affect inclusion
    // (requirement doesn't determine inclusion, visibility does)
    // But we should still process it

    if (!ivConditionsPass) {
      // If IV conditions don't pass, keep the original requirement
      return { shouldInclude: true, newRequirement: requirement };
    }

    // IV_ conditions pass
    // If there are only IV_ conditions, set requirement based on what it would be
    if (otherConditions.length === 0) {
      return { shouldInclude: true, newRequirement: "required" };
    }

    // If there are other conditions, return them
    const newPredicate = otherConditions.join(" and ");
    return { shouldInclude: true, newRequirement: { requiredWhen: newPredicate } };

  } catch (error) {
    console.error("Failed to evaluate IV_ conditions:", error);
    // On error, include with original requirement
    return { shouldInclude: true, newRequirement: requirement };
  }
}
